/* ==========================================================================
   VoxTune — Conversion Funnel
   Program Selection modal → Cart Step 1 → Cart Step 2 (bundle upsells) →
   Shopify checkout. Variant ("program"/DAW) synchronization, dynamic discount
   ladder, duplicate prevention, smart upsells. Registered as VoxTune 'funnel'.

   Triggered by any element with [data-vp-funnel][data-product-handle].
   Config comes from <script data-vp-funnel-config> (rendered by funnel.liquid).
   All modal DOM is built here and only when first needed (conditional weight).
   ========================================================================== */
(function (w, d) {
  'use strict';
  var VT = w.VoxTune;
  if (!VT) return;

  VT.module('funnel', function (VT) {
    var cfg = readConfig();
    var routes = (VT.shop && VT.shop.routes) || {};
    var program = null;        // chosen DAW/program name
    var rootEl = null;         // overlay root
    var cache = {};            // handle -> product.js

    function readConfig() {
      try { return JSON.parse(d.querySelector('script[data-vp-funnel-config]').textContent); }
      catch (e) { return { upsellCollection: '', freeGift: null, boughtTogether: null, payments: [], discount: { enabled: true, ladder: [10, 20, 25, 30] }, programOption: 'Program', multiStep: true, discountCode: '', cta1: 'Continue to checkout', cta2: 'Continue to checkout', ctaSkip: 'No thanks, continue to checkout', trust: '', bannerText: 'Add more — unlock a bigger discount', tierColors: ['#FF9A3D', '#FF7A1A', '#F2620F', '#1FC77D'] }; }
    }
    function esc(s) { var e = d.createElement('span'); e.textContent = s == null ? '' : String(s); return e.innerHTML; }
    function money(c) { return VT.money ? VT.money(c) : '$' + (c / 100).toFixed(2); }
    // Distinct colour per discount tier (Cart 2 banner). n is 1-based item count.
    function tierColor(n) { var t = cfg.tierColors || []; if (!t.length) return 'var(--vp-accent,#FF9A3D)'; return t[Math.min(Math.max(n, 1), t.length) - 1] || 'var(--vp-accent,#FF9A3D)'; }
    // Strict program/DAW variant match (no fallback) — used to skip upsells that
    // don't offer the customer's selected software.
    function variantForProgramStrict(p, prog) { return (p.variants || []).filter(function (v) { return [v.option1, v.option2, v.option3].some(function (o) { return o && String(o).toLowerCase() === String(prog).toLowerCase(); }); })[0] || null; }

    /* ---- data fetch ---------------------------------------------------- */
    // Cache the in-flight promise (not just the resolved value) so prefetch +
    // the real click dedupe to a single request; clear on failure so a retry
    // can re-fetch.
    // Adapt a Product-Data-Layer product into the shape openProgram + upsells
    // expect (incl. option1/2/3 for variantForProgramStrict). Lets "Pick your
    // software" render with ZERO network when the product is already on the page.
    function adaptDataProduct(dp) {
      return {
        id: dp.id, title: dp.title, handle: dp.handle, url: dp.url, price: dp.price,
        featuredImage: dp.featuredImage || null,
        options: dp.optionNames || [],
        variants: (dp.variants || []).map(function (v) {
          var o = v.options || [];
          return { id: v.id, title: v.title, price: v.price, available: v.available, options: o, option1: o[0], option2: o[1], option3: o[2], sku: v.sku };
        })
      };
    }
    function fetchProduct(handle) {
      if (cache[handle]) return cache[handle];
      // Synchronous data-layer hit (hydrated from on-page JSON islands) → resolve
      // instantly, no round-trip, so the modal opens with the DAW grid already there.
      try {
        var dm = VT.use && VT.use('data');
        var dp = dm && dm.get && dm.get(handle);
        if (dp && dp.variants && dp.variants.length) { cache[handle] = Promise.resolve(adaptDataProduct(dp)); return cache[handle]; }
      } catch (e) {}
      var p = fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
      cache[handle] = p; p.catch(function () { delete cache[handle]; });
      return p;
    }
    var collCache = {};
    function fetchCollection(url) {
      if (collCache[url]) return collCache[url];
      var p = fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
      collCache[url] = p; p.catch(function () { delete collCache[url]; });
      return p;
    }
    function upsellUrl(limit) { return '/collections/' + cfg.upsellCollection + '/products.json?limit=' + limit; }
    function getCart() { return fetch((routes.cart || '/cart') + '.js').then(function (r) { return r.json(); }); }
    function addToCart(items) {
      return fetch(routes.cartAdd || '/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) { return r.json().then(function (data) { if (!r.ok) throw data; return data; }); });
    }
    function clearCart() {
      return fetch((routes.cart || '/cart') + '/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }).then(function (r) { return r.json(); });
    }
    // Per-item remove: set one line (by its line key) to a new quantity (0 = remove).
    function changeCart(key, qty) {
      return fetch((routes.cart || '/cart') + '/change.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      }).then(function (r) { return r.json(); });
    }
    // Remember which cart step the customer last saw so the header cart icon
    // reopens the same one within the session (defaults to step 1).
    var STEP_KEY = 'vp_cart_step';
    function setStep(n) { try { sessionStorage.setItem(STEP_KEY, String(n)); } catch (e) {} }
    function lastStep() { var s; try { s = parseInt(sessionStorage.getItem(STEP_KEY), 10); } catch (e) {} return s === 2 ? 2 : 1; }

    function programOptionIndex(p) {
      // product.js options is an array of {name,position,values} OR of strings.
      // Normalise names so a "Software:" config still matches a "Software" option
      // (trailing colon/space + case ignored) — otherwise the picker is skipped
      // and the product is added on its first variant (e.g. Logic Pro X).
      function norm(o) { return String(o == null ? '' : (o.name != null ? o.name : o)).toLowerCase().replace(/[\s:]+$/, '').replace(/^\s+/, ''); }
      var opts = p.options || [];
      var want = norm(cfg.programOption || 'Program');
      var i, n;
      for (i = 0; i < opts.length; i++) { if (norm(opts[i]) === want) return i; }                                   // exact (normalised)
      for (i = 0; i < opts.length; i++) { n = norm(opts[i]); if (n && want && (n.indexOf(want) > -1 || want.indexOf(n) > -1)) return i; } // partial
      // Last resort: the only real variant axis (ignore Shopify's default "Title").
      var real = [];
      for (i = 0; i < opts.length; i++) { if (norm(opts[i]) !== 'title') real.push(i); }
      if (real.length === 1) return real[0];
      return -1;
    }
    function programsFor(p) {
      var idx = programOptionIndex(p);
      if (idx < 0) return [];
      var seen = {}, out = [];
      (p.variants || []).forEach(function (v) {
        if (v.available === false) return;
        var val = v.options[idx];
        if (val && !seen[val]) { seen[val] = 1; out.push({ name: val, variant: v }); }
      });
      return out;
    }
    function variantForProgram(p, prog) {
      var idx = programOptionIndex(p);
      if (idx < 0) return (p.variants || [])[0];
      return (p.variants || []).filter(function (v) {
        return v.available !== false && String(v.options[idx]).toLowerCase() === String(prog).toLowerCase();
      })[0] || null;
    }

    /* ---- overlay scaffold --------------------------------------------- */
    function overlay() {
      if (rootEl) return rootEl;
      rootEl = d.createElement('div');
      rootEl.className = 'vp-funnel-root';
      rootEl.setAttribute('hidden', '');
      rootEl.innerHTML = '<div class="vp-funnel-scrim" data-close style="position:fixed;inset:0;z-index:110;background:rgba(6,7,9,.78);backdrop-filter:blur(4px);animation:vpFade .25s both"></div><div class="vp-funnel-sheet" role="dialog" aria-modal="true" aria-label="Checkout" tabindex="-1" style="position:fixed;left:0;right:0;bottom:0;margin:0 auto;z-index:111;width:min(600px,100%);max-height:96vh;display:flex;flex-direction:column;background:#fff;border:1px solid var(--vp-border,#E5E3DE);border-bottom:none;border-radius:24px 24px 0 0;animation:vpSheet .42s cubic-bezier(.2,.7,.2,1) both;overflow:hidden"></div>';
      d.body.appendChild(rootEl);
      rootEl.addEventListener('click', function (e) { if (e.target.hasAttribute('data-close')) close(); });
      d.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !rootEl.hidden) close(); });
      // Keep Tab focus inside the sheet while the funnel is open.
      rootEl.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab' || rootEl.hidden) return;
        var s = sheet(); if (!s) return;
        var f = s.querySelectorAll('button:not([disabled]),a[href],input,select,textarea,[tabindex]:not([tabindex="-1"])');
        f = Array.prototype.filter.call(f, function (el) { return el.offsetParent !== null; });
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && d.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && d.activeElement === last) { e.preventDefault(); first.focus(); }
      });
      return rootEl;
    }
    function sheet() { return overlay().querySelector('.vp-funnel-sheet'); }
    var lastFocus = null;
    function open() { var o = overlay(); if (o.hidden) lastFocus = d.activeElement; o.hidden = false; d.body.style.overflow = 'hidden'; try { sheet().focus(); } catch (e) {} }
    function close() { if (rootEl) rootEl.hidden = true; d.body.style.overflow = ''; if (lastFocus && lastFocus.focus) lastFocus.focus(); VT.emit('sound:profile', 'home'); }

    function header(title) {
      return '<div style="display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid var(--vp-border,#E5E3DE);flex:0 0 auto">' +
        '<span style="font-family:var(--vp-font-display,sans-serif);font-size:24px;text-transform:uppercase">' + esc(title) + '</span>' +
        '<button type="button" data-close aria-label="Close" style="width:34px;height:34px;border:1px solid var(--vp-border,#E5E3DE);background:#fff;cursor:pointer;display:grid;place-items:center">✕</button></div>';
    }
    // Program-selection header: centred title + circular close (reference layout).
    function progHead() {
      return '<div class="vp-prog-head" style="position:relative;display:flex;align-items:center;justify-content:center;padding:18px 56px;border-bottom:1px solid var(--vp-border,#E5E3DE);flex:0 0 auto">' +
        '<span style="font-family:var(--vp-font-display,sans-serif);font-size:clamp(20px,5.2vw,26px);text-transform:uppercase;text-align:center;line-height:1.05">Pick your software</span>' +
        '<button type="button" data-close aria-label="Close" class="vp-prog-x" style="position:absolute;right:16px;top:50%;transform:translateY(-50%);width:38px;height:38px;border:1px solid var(--vp-border,#E5E3DE);background:#fff;color:var(--vp-ink,#0A0B0D);cursor:pointer;display:grid;place-items:center;border-radius:50%;font-size:15px">✕</button></div>';
    }

    /* ---- 1. Program selection ----------------------------------------- */
    function openProgram(handle) {
      open(); VT.emit('sound:profile', 'program');
      sheet().innerHTML = progHead() + '<div style="padding:30px;text-align:center;color:var(--vp-muted,#8A8C93)">Loading…</div>';
      fetchProduct(handle).then(function (p) {
        var progs = programsFor(p);
        VT.track('ProductViewed', { product_id: p.id, handle: handle });
        if (!progs.length) { // no program option → straight to cart
          var v = (p.variants || []).filter(function (x) { return x.available !== false; })[0] || p.variants[0];
          return addAndCart(p, v);
        }
        var grid = progs.map(function (pr, i) {
          var initial = esc((pr.name || '?').trim().charAt(0).toUpperCase());
          var logo = (cfg.programLogos || {})[(pr.name || '').trim().toLowerCase()];
          var ico = logo
            ? '<span class="vp-prog-ico" style="width:32px;height:32px;border-radius:8px;background:#fff;border:1px solid var(--vp-border,#E5E3DE);display:grid;place-items:center;overflow:hidden;flex:0 0 auto"><img src="' + esc(logo) + '" alt="" style="width:100%;height:100%;object-fit:contain;padding:3px"></span>'
            : '<span class="vp-prog-ico" style="width:30px;height:30px;border-radius:8px;background:var(--vp-ink,#0A0B0D);color:#fff;display:grid;place-items:center;font-family:var(--vp-font-display,sans-serif);font-size:14px;flex:0 0 auto">' + initial + '</span>';
          return '<button type="button" class="vp-prog" data-sfx="daw" data-i="' + i + '" data-vid="' + pr.variant.id + '" data-name="' + esc(pr.name) + '" style="display:flex;align-items:center;gap:12px;border:1px solid var(--vp-border,#E5E3DE);background:#fff;cursor:pointer;font-family:inherit;font-weight:700;font-size:15px;padding:14px 16px;text-align:left">' +
            ico +
            '<span class="vp-prog-name" style="flex:1;min-width:0;line-height:1.15">' + esc(pr.name) + '</span><span class="vp-prog-check" style="width:24px;height:24px;border-radius:50%;border:1px solid var(--vp-border,#E5E3DE);display:grid;place-items:center;flex:0 0 auto"></span></button>';
        }).join('');
        sheet().innerHTML = progHead() +
          '<div class="vp-prog-sub" style="padding:8px 22px 0;color:var(--vp-muted,#8A8C93);font-size:14px;text-align:center">We\'ll prep the right preset format for ' + esc(p.title) + '.</div>' +
          '<div class="vp-scroll vp-prog-grid" style="overflow-y:auto;padding:20px 22px;display:grid;grid-template-columns:1fr 1fr;gap:10px">' + grid + '</div>' +
          '<div style="padding:0 22px 26px;flex:0 0 auto"><button type="button" class="vp-prog-continue" disabled style="width:100%;background:var(--vp-accent,#FF9A3D);color:#0A0B0D;border:none;cursor:pointer;font-weight:800;font-size:17px;padding:18px;text-transform:uppercase;opacity:.5">Select a program</button></div>';
        var chosenVid = null, chosenP = p;
        sheet().querySelectorAll('.vp-prog').forEach(function (b) {
          b.addEventListener('click', function () {
            sheet().querySelectorAll('.vp-prog').forEach(function (x) { x.classList.remove('vp-prog-on'); x.setAttribute('aria-pressed', 'false'); });
            b.classList.add('vp-prog-on'); b.setAttribute('aria-pressed', 'true');
            chosenVid = b.dataset.vid; program = b.dataset.name;
            var cont = sheet().querySelector('.vp-prog-continue'); cont.disabled = false; cont.style.opacity = '1'; cont.textContent = 'Continue with ' + program;
            VT.track('ProgramSelected', { product_id: p.id, program: program });
          });
        });
        sheet().querySelector('.vp-prog-continue').addEventListener('click', function () {
          if (!chosenVid) return;
          var btn = this; if (btn.__busy) return; btn.__busy = true; btn.disabled = true; btn.style.opacity = '.7'; btn.textContent = 'Adding…';
          var v = (chosenP.variants || []).filter(function (x) { return String(x.id) === String(chosenVid); })[0];
          addAndCart(chosenP, v).catch(function () { btn.__busy = false; btn.disabled = false; btn.style.opacity = '1'; btn.textContent = 'Continue with ' + program; });
        });
      }).catch(function () {
        // Network error loading the product (or the straight-to-cart add failed).
        sheet().innerHTML = header('Something went wrong') + '<div style="padding:30px 22px;text-align:center;color:var(--vp-muted,#8A8C93)">We couldn\'t load this right now. Please close and try again.</div>';
      });
    }

    // Optimistic Cart #1: paint the chosen product instantly from cached data
    // (no network), then reconcile with the real cart once /cart/add.js +
    // /cart.js resolve. Makes the drawer appear within a frame of "Continue".
    function openCart1Skeleton(p, variant) {
      open(); setStep(1); VT.emit('sound:profile', 'cart');
      var img = (p && (p.featuredImage || p.featured_image || (p.images && p.images[0] && (p.images[0].src || p.images[0])))) || '';
      var price = (variant && variant.price) || (p && p.price) || 0;
      var line = '<div style="display:flex;align-items:center;gap:12px;padding:16px 22px;border-bottom:1px solid var(--vp-border,#E5E3DE)">' +
        (img ? '<img src="' + esc(img + (img.indexOf('?') < 0 ? '?width=120' : '&width=120')) + '" alt="" width="48" height="56" style="width:48px;height:56px;object-fit:cover;border:1px solid var(--vp-border,#E5E3DE)">' : '') +
        '<div style="flex:1;min-width:0"><div style="font-weight:800;font-size:15px">' + esc(p ? p.title : '') + '</div><div style="font-size:12px;color:var(--vp-muted,#8A8C93)">' + esc(program || '') + '</div></div>' +
        '<div style="font-weight:800">' + money(price) + '</div></div>';
      sheet().innerHTML = header('Your cart') +
        '<div class="vp-scroll" style="overflow-y:auto;flex:1">' + line +
          '<div class="vp-mono" style="padding:16px 22px;text-align:center;color:var(--vp-muted,#8A8C93);font-size:12px;letter-spacing:.08em">Updating your cart…</div>' +
        '</div>' +
        '<div style="flex:0 0 auto;border-top:1px solid var(--vp-border,#E5E3DE);padding:18px 22px">' +
          '<div aria-hidden="true" style="height:54px;border-radius:2px;background:linear-gradient(90deg,#F1EFEA,#FBFAF7,#F1EFEA);background-size:200% 100%;animation:vpShimmer 1.1s linear infinite"></div>' +
        '</div>';
    }
    function addAndCart(p, variant) {
      if (!variant) return Promise.resolve();
      VT.emit('sound:play', 'cart');
      openCart1Skeleton(p, variant);   // instant — no waiting on the network
      return addToCart([{ id: variant.id, quantity: 1 }]).then(function () {
        VT.track('AddToCart', { product_id: p.id, variant_id: variant.id, value: (variant.price || 0) / 100, program: program });
        return getCart();
      }).then(function (cart) { VT.emit('cart:updated', cart); openCart1(cart); })
        // Re-fetch the real cart on failure (never re-adds → no duplicate items).
        .catch(function () { showCartError(function () { openCart1(); }); });
    }

    /* ---- discount ladder ---------------------------------------------- */
    function discountPct(count) {
      if (!cfg.discount || !cfg.discount.enabled) return 0;
      var l = cfg.discount.ladder || [];
      if (!l.length || count < 1) return 0;
      // The ladder is indexed by TOTAL item count (discount_1 = 1 product,
      // discount_2 = 2 products …), so the discount for N items = ladder[N-1],
      // capped at the top tier for 4+. With [0,10,20,30] → 1:0 2:10 3:20 4+:30.
      return l[Math.min(count, l.length) - 1] || 0;
    }
    // A line's PRE-discount price in cents. Prefer original_line_price (before
    // ANY Shopify discount) so the tier preview never stacks on top of a price
    // Shopify already reduced; fall back to line_price / final_line_price.
    function lineOriginal(it) {
      if (!it) return 0;
      if (it.original_line_price != null) return it.original_line_price;
      if (it.line_price != null) return it.line_price;
      return it.final_line_price || 0;
    }
    // Single source of truth for the cart's tier discount — shared by both cart
    // steps' rows, subtotal, savings line, progress meter and the upsell CTA.
    // Built off ORIGINAL prices so the preview shows exactly one tier discount
    // (the same one the checkout discount code will apply), never a double one.
    function getCartTierDiscount(cart) {
      var items = (cart && cart.items) || [];
      var totalQuantity = items.reduce(function (s, it) { return s + (it.quantity || 0); }, 0);
      var discountPercent = discountPct(totalQuantity);
      var subtotal = (cart && cart.original_total_price != null)
        ? cart.original_total_price
        : items.reduce(function (s, it) { return s + lineOriginal(it); }, 0);
      var discountAmount = Math.round(subtotal * discountPercent / 100);
      return {
        totalQuantity: totalQuantity,
        discountPercent: discountPercent,
        subtotal: subtotal,
        discountAmount: discountAmount,
        discountedSubtotal: subtotal - discountAmount,
        nextDiscount: discountPct(totalQuantity + 1)
      };
    }

    /* ---- cart redesign helpers (visual only) -------------------------- */
    // Display-only cleaner: "The Mainstream Vocal Pack" → "Mainstream",
    // "The Drake Preset" → "Drake". Never mutates product data — only the
    // label shown in the compact cart rows / upsell cards.
    function shortName(t) {
      var raw = String(t == null ? '' : t).trim();
      var s = raw.indexOf('|') > -1 ? raw.split('|')[0].trim() : raw;   // "Drake | Vocal Preset" → "Drake"
      s = s.replace(/^the\s+/i, '').replace(/(\s+(vocal\s+preset|vocal\s+pack|preset|pack|bundle))+$/i, '').trim();
      return s || raw;
    }
    function cartGrab() { return '<div class="vpc-grab" aria-hidden="true"><span></span></div>'; }
    function cartHeader(title) {
      return '<div class="vpc-head"><span class="vpc-head-t">' + esc(title) + '</span>' +
        '<button type="button" data-close class="vpc-head-x" aria-label="Close">✕</button></div>';
    }
    // One compact selected-product row, shared by Cart #1 and Cart #2. Keeps the
    // .vp-rm hook + data-key so the existing remove wiring is untouched.
    function cartRowHTML(it, pct) {
      var base = lineOriginal(it);
      var now = Math.round(base * (1 - pct / 100));
      var daw = it.variant_title && String(it.variant_title).toLowerCase() !== 'default title' ? it.variant_title : '';
      return '<div class="vpc-row">' +
        (it.image ? '<img class="vpc-row-img" src="' + esc(it.image + '&width=120') + '" alt="" width="46" height="54">' : '<span class="vpc-row-img"></span>') +
        '<div class="vpc-row-main"><div class="vpc-row-name">' + esc(shortName(it.product_title)) + '</div>' +
          (daw ? '<div class="vpc-row-daw">' + esc(daw) + '</div>' : '') + '</div>' +
        '<div class="vpc-row-price">' +
          (pct ? '<span class="vpc-row-was">' + money(base) + '</span><span class="vpc-row-now disc">' + money(now) + '</span>'
               : '<span class="vpc-row-now">' + money(now) + '</span>') +
        '</div>' +
        '<button type="button" class="vp-rm vpc-rm" data-key="' + esc(it.key) + '" aria-label="Remove ' + esc(it.product_title) + '" title="Remove">×</button>' +
      '</div>';
    }
    // The reference discount box: hero %, per-preset price, and a progress line
    // whose markers are product thumbnails (current items filled, upcoming
    // upsells dimmed). Pure derived view of getCartTierDiscount — no new logic.
    function discountBoxHTML(c, tier, upThumbs) {
      var l = (cfg.discount && cfg.discount.ladder) || [];
      var nodes = l.length || 1;
      var count = tier.totalQuantity, pct = tier.discountPercent, mx = maxPct();
      var items = (c && c.items) || [];
      upThumbs = upThumbs || [];
      var frac = nodes > 1 ? Math.max(0, Math.min(count, nodes) - 1) / (nodes - 1) : 0;
      var hero = pct > 0 ? (pct + '% OFF') : ('Up to ' + mx + '%');
      var pp = count > 0 ? money(Math.round(tier.discountedSubtotal / count)) : '';
      var ui = 0, marks = '';
      for (var i = 0; i < nodes; i++) {
        var reached = count >= (i + 1), img = '';
        if (reached) { var it = items[i]; img = it && it.image ? (it.image + '&width=120') : ''; }
        else { var ut = upThumbs[ui++]; img = ut ? (ut + (ut.indexOf('?') < 0 ? '?width=120' : '&width=120')) : ''; }
        marks += '<span class="vpc-node ' + (reached ? 'on' : 'dim') + (img ? '' : ' empty') + '">' + (img ? '<img src="' + esc(img) + '" alt="">' : '') + '</span>';
      }
      return '<div class="vpc-box">' +
        '<div class="vpc-box-top">' +
          '<div><div class="vpc-box-hd">' + esc(hero) + ' <span class="vpc-arrow">→</span></div>' +
            '<div class="vpc-box-sub">One-time offer · Unlock up to ' + mx + '%</div></div>' +
          (pp ? '<div class="vpc-pp"><div class="vpc-pp-amt">' + pp + '</div><div class="vpc-pp-lab">Per preset</div></div>' : '') +
        '</div>' +
        '<div class="vpc-track"><span class="vpc-track-fill" style="width:calc((100% - 48px) * ' + frac + ')"></span>' + marks + '</div>' +
      '</div>';
    }
    // Recoverable error state for a failed cart fetch (with a retry button).
    function showCartError(retry) {
      sheet().innerHTML = header('Your cart') +
        '<div style="padding:44px 22px;text-align:center;color:var(--vp-muted,#8A8C93)">' +
          '<p style="margin:0 0 16px;font-size:14px">Something went wrong loading your cart.</p>' +
          '<button type="button" class="vp-cart-retry" style="background:var(--vp-ink,#0A0B0D);color:#fff;border:none;cursor:pointer;font-weight:800;font-size:14px;padding:13px 24px;text-transform:uppercase">Try again</button>' +
        '</div>';
      var r = sheet().querySelector('.vp-cart-retry'); if (r) r.addEventListener('click', retry);
    }

    /* ---- discount code box (Cart 1 + Cart 2) -------------------------- */
    // Customer-entered code (newsletter / promo). Persisted for the session and
    // passed to checkout via Shopify's /discount/{code}?redirect=… pattern so the
    // automatic quantity tiers stay intact; Shopify decides any stacking.
    var PROMO_KEY = 'vp_promo_code';
    function readPromo() { try { return sessionStorage.getItem(PROMO_KEY) || ''; } catch (e) { return ''; } }
    var promoCode = readPromo();
    function setPromo(c) { promoCode = String(c == null ? '' : c).trim(); try { sessionStorage.setItem(PROMO_KEY, promoCode); } catch (e) {} }
    function clearPromo() { promoCode = ''; try { sessionStorage.removeItem(PROMO_KEY); } catch (e) {} }
    function promoInnerHTML() {
      if (promoCode) {
        return '<div style="display:flex;align-items:center;justify-content:space-between;gap:10px;border:1px solid var(--vp-accent,#FF9A3D);background:rgba(255,154,61,.08);border-radius:8px;padding:10px 12px">' +
          '<span class="vp-mono" style="font-size:12px;color:var(--vp-ink,#0A0B0D);min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">Code applied: <strong>' + esc(promoCode) + '</strong></span>' +
          '<button type="button" data-promo-remove aria-label="Remove discount code" style="flex:0 0 auto;width:24px;height:24px;border:1px solid var(--vp-border,#E5E3DE);background:#fff;color:var(--vp-muted,#8A8C93);border-radius:50%;cursor:pointer;display:grid;place-items:center;font-size:14px;line-height:1">×</button></div>';
      }
      return '<button type="button" data-promo-toggle style="background:none;border:none;cursor:pointer;color:var(--vp-muted,#8A8C93);font-size:12px;text-decoration:underline;font-family:inherit;padding:2px 0">Have a discount code?</button>' +
        '<div data-promo-form hidden style="display:flex;gap:8px;margin-top:8px">' +
          '<input type="text" data-promo-input placeholder="Enter discount code" autocomplete="off" autocapitalize="characters" style="flex:1;min-width:0;font-family:inherit;font-size:14px;padding:11px 12px;border:1px solid var(--vp-border,#E5E3DE);border-radius:8px;background:#fff;color:var(--vp-ink,#0A0B0D)">' +
          '<button type="button" data-promo-apply style="flex:0 0 auto;background:var(--vp-ink,#0A0B0D);color:#fff;border:none;cursor:pointer;font-weight:800;font-size:13px;padding:0 18px;border-radius:8px;text-transform:uppercase">Apply</button>' +
        '</div>';
    }
    function wirePromo(box) {
      if (!box) return;
      box.innerHTML = promoInnerHTML();
      var toggle = box.querySelector('[data-promo-toggle]');
      if (toggle) toggle.addEventListener('click', function () { var f = box.querySelector('[data-promo-form]'); if (f) { f.hidden = false; toggle.style.display = 'none'; var i = box.querySelector('[data-promo-input]'); if (i) i.focus(); } });
      var apply = box.querySelector('[data-promo-apply]');
      if (apply) apply.addEventListener('click', function () { var i = box.querySelector('[data-promo-input]'); var v = (i && i.value || '').trim(); if (!v) { if (i) i.focus(); return; } setPromo(v); wirePromo(box); VT.emit('sound:play', 'click'); });
      var input = box.querySelector('[data-promo-input]');
      if (input) input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); var a = box.querySelector('[data-promo-apply]'); if (a) a.click(); } });
      var rm = box.querySelector('[data-promo-remove]');
      if (rm) rm.addEventListener('click', function () { clearPromo(); wirePromo(box); VT.emit('sound:play', 'click'); });
    }

    /* ---- 2. Cart Step 1 ------------------------------------------------ */
    function openCart1(cart) {
      open(); setStep(1); VT.emit('sound:profile', 'cart');
      (cart ? Promise.resolve(cart) : getCart()).then(function (c) {
        var tier = getCartTierDiscount(c);
        var count = tier.totalQuantity;
        var pct = tier.discountPercent;
        var lines = (c.items || []).map(function (it) {
          var lineBase = lineOriginal(it);
          var lineDisc = Math.round(lineBase * (1 - pct / 100));
          return '<div style="display:flex;align-items:center;gap:12px;padding:16px 22px;border-bottom:1px solid var(--vp-border,#E5E3DE)">' +
            (it.image ? '<img src="' + esc(it.image + '&width=120') + '" alt="" width="48" height="56" style="width:48px;height:56px;object-fit:cover;border:1px solid var(--vp-border,#E5E3DE)">' : '') +
            '<div style="flex:1;min-width:0"><div style="font-weight:800;font-size:15px">' + esc(it.product_title) + '</div><div style="font-size:12px;color:var(--vp-muted,#8A8C93)">' + esc(it.variant_title || '') + '</div></div>' +
            '<div style="text-align:right;white-space:nowrap">' + (pct ? '<span class="vp-mono" style="font-size:11px;color:#A6A39C;text-decoration:line-through;margin-right:6px">' + money(lineBase) + '</span>' : '') + '<span style="font-weight:800' + (pct ? ';color:' + tierColor(count) : '') + '">' + money(lineDisc) + '</span>' + (pct ? '<div style="margin-top:3px"><span class="vp-mono" style="font-size:9px;font-weight:800;color:#fff;background:' + tierColor(count) + ';border-radius:4px;padding:1px 5px">' + pct + '% OFF</span></div>' : '') + '</div>' +
            '<button type="button" class="vp-rm" data-key="' + esc(it.key) + '" aria-label="Remove ' + esc(it.product_title) + '" title="Remove" style="width:26px;height:26px;border:1px solid var(--vp-border,#E5E3DE);background:#fff;color:var(--vp-muted,#8A8C93);border-radius:50%;cursor:pointer;display:grid;place-items:center;font-size:16px;line-height:1;flex:0 0 auto">×</button>' +
          '</div>';
        }).join('') || '<div style="padding:50px 22px;text-align:center;color:var(--vp-muted,#8A8C93)">Your cart is empty.</div>';

        var pay = (cfg.payments || []).map(function (svg) { return '<span class="vp-pay-ic" style="display:inline-flex;align-items:center">' + svg + '</span>'; }).join('');
        // Warm the upsell collection now so Cart 2 opens instantly on Continue.
        if (cfg.upsellCollection) fetchCollection(upsellUrl(30));

        sheet().innerHTML = header(count > 0 ? (count + ' item' + (count === 1 ? '' : 's') + ' in cart') : 'Your cart') +
          '<div class="vp-scroll" style="overflow-y:auto;flex:1">' + lines +
            '<div class="vp-upsell-slot" style="padding:6px 22px"></div>' +
            (cfg.freeGift ? '<div style="padding:0 22px 14px"><div class="vp-mono" style="font-size:11px;color:var(--vp-accent,#FF9A3D);margin-bottom:8px">ADD A FREE GIFT</div>' +
              '<button type="button" class="vp-free-gift" style="width:100%;display:flex;align-items:center;gap:12px;border:1px solid var(--vp-border,#E5E3DE);border-radius:10px;padding:12px;background:#fff;cursor:pointer;text-align:left;font-family:inherit">' +
              (cfg.freeGift.image ? '<img src="' + esc(cfg.freeGift.image) + '" alt="" width="38" height="38" style="width:38px;height:38px;object-fit:cover;border-radius:6px">' : '') +
              '<div style="flex:1"><div style="font-weight:800;font-size:14px">' + esc(cfg.freeGift.title) + ' <span style="background:var(--vp-accent,#FF9A3D);color:#0A0B0D;font-size:9px;padding:2px 6px;border-radius:4px">FREE</span></div><div style="font-size:12px;color:var(--vp-muted,#8A8C93)">Tap to add — yours with this order</div></div>' +
              '<span style="width:30px;height:30px;border-radius:50%;background:var(--vp-accent,#FF9A3D);color:#0A0B0D;display:grid;place-items:center;font-weight:800;flex:0 0 auto">+</span></button></div>' : '') +
            (count > 0 ? '<div style="padding:2px 22px 14px;text-align:right"><button type="button" class="vp-clear-cart" style="background:none;border:none;cursor:pointer;color:var(--vp-muted,#8A8C93);font-size:11px;text-decoration:underline;opacity:.55;font-family:inherit;padding:4px 0">' + esc(cfg.clearLabel || 'Clear cart') + '</button></div>' : '') +
          '</div>' +
          '<div style="flex:0 0 auto;border-top:1px solid var(--vp-border,#E5E3DE);padding:18px 22px">' +
            (pct
              ? '<div style="display:flex;justify-content:space-between;align-items:baseline"><span style="font-size:13px;color:var(--vp-muted,#8A8C93)">Subtotal</span><span style="display:inline-flex;align-items:baseline;gap:8px"><span class="vp-mono" style="font-size:13px;color:#A6A39C;text-decoration:line-through">' + money(tier.subtotal) + '</span><span style="font-family:var(--vp-font-display,sans-serif);font-size:26px;color:' + tierColor(count) + '">' + money(tier.discountedSubtotal) + '</span></span></div>' +
                '<div class="vp-mono" style="text-align:right;font-size:11px;color:var(--vp-accent,#FF9A3D);margin:3px 0 12px">You save ' + money(tier.discountAmount) + ' · ' + pct + '%</div>'
              : '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px"><span style="font-size:13px;color:var(--vp-muted,#8A8C93)">Subtotal</span><span style="font-family:var(--vp-font-display,sans-serif);font-size:26px">' + money(tier.subtotal) + '</span></div>') +
            (pay ? '<div style="display:flex;gap:6px;flex-wrap:wrap;justify-content:center;margin:12px 0">' + pay + '</div>' : '') +
            (cfg.trust ? '<div class="vp-mono" style="text-align:center;font-size:10px;letter-spacing:.08em;color:var(--vp-muted,#8A8C93);text-transform:uppercase;margin:8px 0 4px">' + esc(cfg.trust) + '</div>' : '') +
            '<div class="vp-promo" data-promo style="margin:0 0 12px"></div>' +
            '<button type="button" class="vp-to-cart2" style="width:100%;background:var(--vp-ink,#0A0B0D);color:#fff;border:none;cursor:pointer;font-weight:800;font-size:16px;padding:17px;text-transform:uppercase">' + (cfg.multiStep ? (cfg.cta1 || 'Continue to checkout') : 'Checkout') + '</button>' +
          '</div>';

        wirePromo(sheet().querySelector('[data-promo]'));
        // "Frequently bought together" (dedicated product) takes the upsell slot;
        // otherwise fall back to the first eligible upsell-collection product.
        var upslot = sheet().querySelector('.vp-upsell-slot');
        if (cfg.cart1Upsell !== false) { if (!renderBoughtTogether(c, upslot)) renderAltUpsell(c, upslot); }
        var fg = sheet().querySelector('.vp-free-gift');
        if (fg) fg.addEventListener('click', function () { if (fg.__busy) return; fg.__busy = true; fg.disabled = true; fg.style.opacity = '.6'; VT.emit('sound:play', 'cart'); addToCart([{ id: cfg.freeGift.variantId, quantity: 1 }]).then(getCart).then(function (cc) { VT.emit('cart:updated', cc); openCart1(cc); }).catch(function () { fg.__busy = false; fg.disabled = false; fg.style.opacity = ''; }); });
        var clr = sheet().querySelector('.vp-clear-cart');
        if (clr) clr.addEventListener('click', function () { if (clr.__busy) return; clr.__busy = true; clr.disabled = true; clr.style.opacity = '.3'; clearCart().then(function (cc) { VT.emit('cart:updated', cc); openCart1(cc); }).catch(function () { clr.__busy = false; clr.disabled = false; clr.style.opacity = '.55'; }); });
        sheet().querySelectorAll('.vp-rm').forEach(function (b) {
          b.addEventListener('click', function () {
            if (b.__busy) return; b.__busy = true; b.style.opacity = '.4';
            changeCart(b.getAttribute('data-key'), 0).then(function (cc) { VT.emit('cart:updated', cc); openCart1(cc); }).catch(function () { b.__busy = false; b.style.opacity = ''; });
          });
        });
        sheet().querySelector('.vp-to-cart2').addEventListener('click', function () {
          var btn = this; if (btn.__busy) return; btn.__busy = true; btn.disabled = true;
          if (cfg.multiStep) openCart2(c); else checkout();
        });
      }).catch(function () { showCartError(function () { openCart1(); }); });
    }

    /* ---- 3. Cart Step 2 (bundle upsells + live discount meter) -------- */
    function maxPct() { var l = (cfg.discount && cfg.discount.ladder) || []; return l.length ? l[l.length - 1] : 0; }
    function openCart2(cart) {
      open(); setStep(2); VT.emit('sound:profile', 'upsell');
      (cart ? Promise.resolve(cart) : getCart()).then(function (c) {
        var tier = getCartTierDiscount(c);
        var count = tier.totalQuantity; var pct = tier.discountPercent;
        var rows = (c.items || []).map(function (it) { return cartRowHTML(it, pct); }).join('') ||
          '<div class="vpc-ups-msg" style="padding:46px 0">Your cart is empty.</div>';
        sheet().innerHTML = cartGrab() + cartHeader(count + ' item' + (count === 1 ? '' : 's') + ' in cart') +
          '<div class="vpc-scroll">' +
            discountBoxHTML(c, tier, []) + rows +
            (cfg.cart2Upsell !== false ? '<div class="vpc-ups-lab">' + esc(cfg.bannerText || 'Add more — unlock a bigger discount') + '</div>' +
            '<div class="vp-bundle-grid vpc-ups"><div class="vpc-ups-msg">Finding matches…</div></div>' : '') +
          '</div>' +
          '<div class="vpc-foot">' +
            (pct ? '<div class="vpc-save">You save ' + money(tier.discountAmount) + ' · ' + pct + '%</div>' : '') +
            '<div class="vpc-total">' + (pct ? '<span class="vpc-total-was">' + money(tier.subtotal) + '</span>' : '') + '<span class="vpc-total-now">' + money(tier.discountedSubtotal) + '</span></div>' +
            '<button type="button" class="vp-checkout vpc-cta vpc-cta-accent">' + esc(cfg.cta2 || 'Continue to checkout') + (pct ? ' · save ' + pct + '%' : '') + ' <span>→</span></button>' +
            '<button type="button" class="vp-decline vpc-decline">' + esc(cfg.ctaSkip || 'No thanks, continue to checkout') + '</button>' +
            '<div class="vp-promo vpc-promo" data-promo></div>' +
            (count > 0 ? '<button type="button" class="vp-clear-cart vpc-mini">' + esc(cfg.clearLabel || 'Clear cart') + '</button>' : '') +
          '</div>';
        var bgrid = sheet().querySelector('.vp-bundle-grid'); if (bgrid) renderBundleUpsells(c, bgrid);
        wirePromo(sheet().querySelector('[data-promo]'));
        sheet().querySelector('.vp-decline').addEventListener('click', function () { VT.track('UpsellDeclined', {}); checkout(); });
        sheet().querySelector('.vp-checkout').addEventListener('click', checkout);
        var clr2 = sheet().querySelector('.vp-clear-cart');
        if (clr2) clr2.addEventListener('click', function () { if (clr2.__busy) return; clr2.__busy = true; clr2.disabled = true; clr2.style.opacity = '.3'; clearCart().then(function (cc) { VT.emit('cart:updated', cc); openCart1(cc); }).catch(function () { clr2.__busy = false; clr2.disabled = false; clr2.style.opacity = '.55'; }); });
        sheet().querySelectorAll('.vp-rm').forEach(function (b) {
          b.addEventListener('click', function () {
            if (b.__busy) return; b.__busy = true; b.style.opacity = '.4';
            changeCart(b.getAttribute('data-key'), 0).then(function (cc) { VT.emit('cart:updated', cc); openCart2(cc); }).catch(function () { b.__busy = false; b.style.opacity = ''; });
          });
        });
      }).catch(function () { showCartError(function () { openCart2(); }); });
    }

    /* ---- upsell rendering (exclude cart products, sync program) -------- */
    function cartProductIds(cart) { return (cart.items || []).map(function (i) { return i.product_id; }); }

    // Cart 1 carries exactly ONE configured upsell — no collection / rotating
    // picks. Syncs the chosen DAW/program variant; renders nothing (returns
    // false) when the product is missing or already in the cart.
    function renderSingleUpsell(cart, slot, data, opts) {
      if (!slot || !data || !data.handle) return false;
      if (data.id && cartProductIds(cart).indexOf(data.id) > -1) return false;
      opts = opts || {};
      // CTA text shows the NEXT tier unlocked by adding one more product. At the
      // max tier it reads "Max discount unlocked"; with discounts off, the plain
      // label. Tier comes from the shared helper (same source as the cart math).
      var tier = getCartTierDiscount(cart || {});
      var nextPct = tier.nextDiscount, curPct = tier.discountPercent;
      var discOn = !!(cfg.discount && cfg.discount.enabled && (cfg.discount.ladder || []).length);
      var eyebrow = !discOn ? (opts.label || 'Add & save') : (nextPct > curPct ? ('Add more & save ' + nextPct + '% off') : 'Max discount unlocked');
      slot.innerHTML = '<div style="border:1px dashed var(--vp-accent,#FF9A3D);border-radius:10px;padding:14px;display:flex;align-items:center;gap:12px;background:rgba(255,154,61,.06)">' +
        (data.image ? '<img src="' + esc(data.image) + '" alt="" width="46" height="46" style="width:46px;height:46px;object-fit:cover;border-radius:6px">' : '') +
        '<div style="flex:1;min-width:0"><div style="font-size:11px;color:var(--vp-accent,#FF9A3D);font-weight:800;text-transform:uppercase">' + esc(eyebrow) + '</div><div style="font-weight:800;font-size:14px">' + esc(data.title) + '</div></div>' +
        '<button type="button" class="vp-add-bt" style="background:var(--vp-ink,#0A0B0D);color:#fff;border:none;cursor:pointer;font-weight:800;padding:10px 14px;white-space:nowrap">' + (opts.button ? esc(opts.button) : '+ ' + money(data.price)) + '</button></div>';
      var btBtn = slot.querySelector('.vp-add-bt');
      btBtn.addEventListener('click', function () {
        if (btBtn.__busy) return; btBtn.__busy = true; btBtn.disabled = true; btBtn.style.opacity = '.6';
        VT.emit('sound:play', 'cart');
        fetchProduct(data.handle).then(function (p) {
          var v = program ? (variantForProgramStrict(p, program) || (p.variants || [])[0]) : (p.variants || [])[0];
          if (!v) { btBtn.__busy = false; btBtn.disabled = false; btBtn.style.opacity = ''; return; }
          return addToCart([{ id: v.id, quantity: 1 }]).then(getCart).then(function (cc) { VT.emit('cart:updated', cc); VT.track('UpsellAccepted', { handle: data.handle }); openCart1(cc); });
        }).catch(function () { btBtn.__busy = false; btBtn.disabled = false; btBtn.style.opacity = ''; });
      });
      return true;
    }
    // Primary upsell (the "Vocal Pulse Pack"); shown when it isn't already in the cart.
    function renderBoughtTogether(cart, slot) {
      var bt = cfg.boughtTogether;
      return renderSingleUpsell(cart, slot, bt, { label: bt && bt.label, button: bt && bt.button, showPct: true });
    }
    // Alternative upsell, shown only once the primary product is already in the cart.
    function renderAltUpsell(cart, slot) {
      return renderSingleUpsell(cart, slot, cfg.cart1Alt, { label: (cfg.cart1Alt && cfg.cart1Alt.label) || 'Add & save' });
    }

    function renderBundleUpsells(cart, list) {
      if (!cfg.upsellCollection) { list.innerHTML = '<div class="vpc-ups-msg">No bundle offers configured.</div>'; return; }
      fetchCollection(upsellUrl(30))
        .then(function (data) {
          var inCart = cartProductIds(cart);
          var cands = (data.products || []).filter(function (p) { return inCart.indexOf(p.id) < 0 && (!program || variantForProgramStrict(p, program)); }).slice(0, cfg.upsellCount || 6);
          if (!cands.length) { list.innerHTML = '<div class="vpc-ups-msg">You\'ve got everything — nice.</div>'; return; }
          list.innerHTML = cands.map(function (p) {
            var v = program ? variantForProgramStrict(p, program) : p.variants[0];
            if (!v) return '';
            // Reference design: a clean, short "+ Add" — never the long product
            // title (which wrapped the pill button onto 3 lines).
            var label = '+ Add';
            return '<div class="vpc-ups-card">' +
              '<div class="vpc-ups-thumb">' +
                (p.images && p.images[0] ? '<img src="' + esc(p.images[0].src + '&width=240') + '" alt="" width="240" height="240" loading="lazy">' : '') +
              '</div>' +
              '<div class="vpc-ups-body"><div class="vpc-ups-name">' + esc(shortName(p.title)) + '</div><div class="vpc-ups-price">' + money(v.price * 100) + '</div>' +
                '<button type="button" class="vp-add-bundle vpc-ups-add" data-vid="' + v.id + '">' + label + '</button></div>' +
            '</div>';
          }).join('');
          list.querySelectorAll('.vp-add-bundle').forEach(function (b) {
            b.addEventListener('click', function () {
              if (b.__busy) return; b.__busy = true; b.disabled = true; b.style.opacity = '.6';
              VT.emit('sound:play', 'cart');
              addToCart([{ id: b.dataset.vid, quantity: 1 }]).then(getCart).then(function (cc) { VT.emit('cart:updated', cc); VT.track('UpsellAccepted', { variant_id: b.dataset.vid }); openCart2(); }).catch(function () { b.__busy = false; b.disabled = false; b.style.opacity = ''; });
            });
          });
        }).catch(function () {});
    }

    var checkingOut = false;
    function checkout() {
      if (checkingOut) return; checkingOut = true; VT.emit('sound:play', 'checkout'); VT.track('CheckoutStarted', { code: promoCode || null });
      var base = '/checkout' + (cfg.discountCode ? '?discount=' + encodeURIComponent(cfg.discountCode) : '');
      // A customer code is applied via Shopify's discount route, then redirected to
      // checkout (which also carries the configured backing code). Shopify's own
      // combination rules decide whether they stack.
      w.location.href = promoCode ? ('/discount/' + encodeURIComponent(promoCode) + '?redirect=' + encodeURIComponent(base)) : base;
    }

    /* ---- triggers ------------------------------------------------------ */
    // Handle-based CTA routing (configured in funnel.liquid). Some products
    // don't go through the program/cart flow at all:
    //   Custom Preset  → open the Custom Preset modal (everywhere)
    //   Vocal Pulse    → smooth-scroll to its dedicated section (outside it)
    function handleList(s) { return String(s || '').toLowerCase().split(',').map(function (x) { return x.trim(); }).filter(Boolean); }
    var ctaCustom = handleList(cfg.ctaCustomHandles);
    var ctaVocal = handleList(cfg.ctaVocalHandles);
    function openCustomPreset() {
      try { d.dispatchEvent(new CustomEvent('voxtune:custompreset:open')); }
      catch (e) { var t = d.querySelector('[data-vp-custom-open],a[href*="custom-preset"]'); if (t) t.click(); }
    }
    function scrollToTarget(sel) {
      if (!sel) return;
      var el; try { el = d.querySelector(sel); } catch (e) { el = null; }
      if (!el) return;
      var hv = parseInt(getComputedStyle(d.documentElement).getPropertyValue('--vp-header-h'), 10) || 72;
      var reduce = w.matchMedia && w.matchMedia('(prefers-reduced-motion:reduce)').matches;
      var y = el.getBoundingClientRect().top + w.pageYOffset - hv;
      w.scrollTo({ top: Math.max(0, y), behavior: reduce ? 'auto' : 'smooth' });
    }
    d.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('[data-vp-funnel]');
      if (!t) return;
      var handle = t.getAttribute('data-product-handle');
      if (!handle) { var u = t.getAttribute('data-product-url'); if (u) w.location.href = u; return; }
      var h = handle.toLowerCase();
      // Custom Preset → open the modal instead of the cart (no redirect).
      if (ctaCustom.indexOf(h) > -1) { e.preventDefault(); e.stopPropagation(); openCustomPreset(); return; }
      // Vocal Pulse → scroll to its section, unless we're already inside it.
      if (ctaVocal.indexOf(h) > -1 && !(t.closest && t.closest('[data-section-type="best-seller"]'))) {
        e.preventDefault(); e.stopPropagation(); scrollToTarget(cfg.ctaVocalTarget); return;
      }
      e.preventDefault(); program = null; openProgram(handle);
    });
    // Prefetch the product on intent (hover / touch start) so "Pick your
    // software" renders instantly on click. fetchProduct dedupes + caches.
    function warm(e) {
      var t = e.target.closest && e.target.closest('[data-vp-funnel][data-product-handle]');
      if (t) fetchProduct(t.getAttribute('data-product-handle'));
    }
    d.addEventListener('pointerover', warm, { passive: true });
    d.addEventListener('touchstart', warm, { passive: true });
    // Preload DAW logos once idle so the grid paints instantly the first time.
    function preloadLogos() { var L = cfg.programLogos || {}; Object.keys(L).forEach(function (k) { if (L[k]) { var im = new Image(); im.src = L[k]; } }); }
    if (w.requestIdleCallback) w.requestIdleCallback(preloadLogos); else setTimeout(preloadLogos, 1200);

    // Cart button / mini-cart opens whichever step the customer last saw
    // (persisted per session); falls back to step 1.
    VT.on('funnel:cart', function () { if (lastStep() === 2 && cfg.multiStep) openCart2(); else openCart1(); });

    return { openProgram: openProgram, openCart1: openCart1, openCart2: openCart2, close: close };
  });

  // boot when DOM is ready (module self-registers; first click builds modal)
  VT.ready(function () { VT.use('funnel'); });
})(window, document);
